import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Truck, CreditCard, RotateCcw, Shield, Lock, Wallet } from 'lucide-react';
import { Button } from '@/components/ui/button';
import ProductCard from '@/components/ProductCard';
import HeroBanner from '@/components/HeroBanner';
import FreeShippingPromoBanner from '@/components/FreeShippingPromoBanner';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { apiGetWithRetry } from '@/utils/api';
import { trackAdClick, trackCategoryClick } from '@/utils/analytics';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const categories = [
  {
    name: 'Masculino',
    image: '/products/img_14_new.jpg',
    href: '/produtos/masculino'
  },
  {
    name: 'Feminino',
    image: '/products/img_11_new.jpg',
    href: '/produtos/feminino'
  },
  {
    name: 'Infantil',
    image: '/products/infantil_banner.jpg',
    href: '/produtos/infantil'
  }
];

const benefits = [
  { icon: Truck, title: 'Frete Grátis', description: 'Acima de R$599' },
  { icon: Wallet, title: '10% Cashback', description: 'Em toda compra' },
  { icon: CreditCard, title: 'Até 3x', description: 'Sem juros' },
  { icon: RotateCcw, title: 'Troca Fácil', description: 'Em até 30 dias' },
  { icon: Shield, title: 'Compra Segura', description: '100% protegida' }
];

export default function HomePage() {
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [newProducts, setNewProducts] = useState([]);
  const [favoriteIds, setFavoriteIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  
  // Drag scroll para categorias
  const scrollRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);

  const handleMouseDown = (e) => {
    if (!scrollRef.current) return;
    setIsDragging(true);
    setStartX(e.pageX - scrollRef.current.offsetLeft);
    setScrollLeft(scrollRef.current.scrollLeft);
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleMouseMove = (e) => {
    if (!isDragging || !scrollRef.current) return;
    e.preventDefault();
    const x = e.pageX - scrollRef.current.offsetLeft;
    const walk = (x - startX) * 1.5;
    scrollRef.current.scrollLeft = scrollLeft - walk;
  };

  const fetchFavorites = async () => {
    if (user) {
      try {
        const res = await axios.get(`${API}/favorites`, { withCredentials: true });
        setFavoriteIds(res.data);
      } catch (e) {
        // Silent fail
      }
    }
  };

  useEffect(() => {
    let cancelled = false;
    let retryTimer;

    const fetchProducts = async () => {
      try {
        // Busca resiliente: re-tenta automaticamente em caso de cold start pós-deploy,
        // garantindo que a home nunca apareça sem produtos por falha transitória.
        const [featuredRes, allRes] = await Promise.all([
          apiGetWithRetry('/products', { params: { featured: true } }),
          apiGetWithRetry('/products')
        ]);
        if (cancelled) return;

        setFeaturedProducts(featuredRes.data.slice(0, 8));
        setNewProducts(allRes.data.filter(p => p.is_new).slice(0, 4));
        setLoading(false);
      } catch (e) {
        if (cancelled) return;
        console.error('Error fetching products (irá re-tentar):', e);
        // Mantém o skeleton e re-tenta em background até o backend responder.
        retryTimer = setTimeout(fetchProducts, 4000);
      }
    };

    fetchProducts();
    fetchFavorites();
    return () => {
      cancelled = true;
      if (retryTimer) clearTimeout(retryTimer);
    };
  }, [user]);

  // Categorias com ícones SVG profissionais
  const categoryIcons = [
    { name: 'Masculino', icon: 'masculino', filter: 'masculino', isCategory: true },
    { name: 'Feminino', icon: 'feminino', filter: 'feminino', isCategory: true },
    { name: 'Infantil', icon: 'infantil', filter: 'infantil', isCategory: true },
    { name: 'Fitness', icon: 'fitness', filter: 'fitness' },
    { name: 'Vestidos', icon: 'dress', filter: 'vestidos' },
    { name: 'Calças', icon: 'pants', filter: 'calcas' },
    { name: 'Blusas', icon: 'blouse', filter: 'blusas' },
    { name: 'Camisetas', icon: 'tshirt', filter: 'camisetas' },
    { name: 'Camisas', icon: 'shirt', filter: 'camisas' },
    { name: 'Shorts', icon: 'shorts', filter: 'shorts' },
    { name: 'Bermudas', icon: 'bermuda', filter: 'bermudas' },
  ];

  // Componente de ícone profissional estilo linha
  const CategoryIcon = ({ type }) => {
    const iconClass = "w-5 h-5 md:w-6 md:h-6";
    
    switch(type) {
      case 'fitness':
        // Sports bra / Top esportivo
        return (
          <svg className={iconClass} viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 14C12 14 14 12 18 12L24 16L30 12C34 12 36 14 36 14" stroke="#C41E3A" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M12 14V28C12 30 14 32 18 32H30C34 32 36 30 36 28V14" stroke="#C41E3A" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M18 32V36M30 32V36" stroke="#C41E3A" strokeWidth="2.5" strokeLinecap="round"/>
            <path d="M18 12L14 8M30 12L34 8" stroke="#C41E3A" strokeWidth="2.5" strokeLinecap="round"/>
            <ellipse cx="24" cy="22" rx="6" ry="4" stroke="#C41E3A" strokeWidth="2" strokeDasharray="2 2"/>
          </svg>
        );
      case 'dress':
        // Vestido elegante
        return (
          <svg className={iconClass} viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M20 8C20 8 22 10 24 10C26 10 28 8 28 8" stroke="#C41E3A" strokeWidth="2.5" strokeLinecap="round"/>
            <path d="M20 8L16 14L12 14L16 40H32L36 14L32 14L28 8" stroke="#C41E3A" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M16 14L18 20M32 14L30 20" stroke="#C41E3A" strokeWidth="2" strokeLinecap="round"/>
            <path d="M18 28H30" stroke="#C41E3A" strokeWidth="2" strokeLinecap="round" strokeDasharray="3 2"/>
          </svg>
        );
      case 'pants':
        // Calça
        return (
          <svg className={iconClass} viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M14 8H34V14L30 40H26L24 24L22 40H18L14 14V8Z" stroke="#C41E3A" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M14 14H34" stroke="#C41E3A" strokeWidth="2" strokeLinecap="round"/>
            <circle cx="24" cy="11" r="1.5" fill="#C41E3A"/>
          </svg>
        );
      case 'blouse':
        // Blusa feminina
        return (
          <svg className={iconClass} viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M18 8L14 12L8 10L10 22L14 24V38H34V24L38 22L40 10L34 12L30 8" stroke="#C41E3A" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M18 8C18 8 20 12 24 12C28 12 30 8 30 8" stroke="#C41E3A" strokeWidth="2.5" strokeLinecap="round"/>
            <path d="M20 20V28M24 18V28M28 20V28" stroke="#C41E3A" strokeWidth="1.5" strokeLinecap="round" strokeDasharray="2 3"/>
          </svg>
        );
      case 'tshirt':
        // Camiseta básica
        return (
          <svg className={iconClass} viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M18 8L10 12L8 22L14 24V40H34V24L40 22L38 12L30 8" stroke="#C41E3A" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M18 8C18 8 20 14 24 14C28 14 30 8 30 8" stroke="#C41E3A" strokeWidth="2.5" strokeLinecap="round"/>
          </svg>
        );
      case 'shirt':
        // Camisa social com gola
        return (
          <svg className={iconClass} viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M18 8L12 12L8 10L10 24L14 26V40H34V26L38 24L40 10L36 12L30 8" stroke="#C41E3A" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M18 8L22 14L24 10L26 14L30 8" stroke="#C41E3A" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
            <line x1="24" y1="14" x2="24" y2="40" stroke="#C41E3A" strokeWidth="1.5"/>
            <circle cx="24" cy="20" r="1" fill="#C41E3A"/>
            <circle cx="24" cy="26" r="1" fill="#C41E3A"/>
            <circle cx="24" cy="32" r="1" fill="#C41E3A"/>
          </svg>
        );
      case 'shorts':
        // Shorts
        return (
          <svg className={iconClass} viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 10H36V16L32 34H28L24 22L20 34H16L12 16V10Z" stroke="#C41E3A" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M12 16H36" stroke="#C41E3A" strokeWidth="2" strokeLinecap="round"/>
            <path d="M22 10V14M26 10V14" stroke="#C41E3A" strokeWidth="1.5" strokeLinecap="round"/>
          </svg>
        );
      case 'bermuda':
        // Bermuda masculina (mais comprida que shorts)
        return (
          <svg className={iconClass} viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 8H36V14L33 38H28L24 20L20 38H15L12 14V8Z" stroke="#C41E3A" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M12 14H36" stroke="#C41E3A" strokeWidth="2" strokeLinecap="round"/>
            <path d="M18 14L16 26" stroke="#C41E3A" strokeWidth="1.5" strokeLinecap="round" strokeDasharray="2 3"/>
          </svg>
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen" data-testid="home-page">
      {/* Banner promocional: frete grátis 2+ peças com cronômetro 24h */}
      <FreeShippingPromoBanner />
      {/* Hero Section - Banner com carrossel de vídeo */}
      <HeroBanner />

      {/* Category Icons Section - Botões de categoria */}
      <section className="bg-[#0d0d0d] py-6 md:py-10" data-testid="category-icons-section">
        <div className="max-w-7xl mx-auto px-4">
          <div 
            ref={scrollRef}
            className={`flex justify-start gap-2 md:gap-3 overflow-x-auto pb-3 category-scroll ${isDragging ? 'cursor-grabbing' : 'cursor-grab'}`}
            onMouseDown={handleMouseDown}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            onMouseMove={handleMouseMove}
          >
            {categoryIcons.map((cat) => (
              <Link
                key={cat.name}
                to={cat.isCategory ? `/produtos/${cat.filter}` : `/produtos?subcategory=${cat.filter}`}
                className="flex items-center gap-2 px-4 py-2 md:px-5 md:py-2.5 rounded-full bg-[#1a1a1a] border border-white/10 transition-all duration-300 hover:border-[#C41E3A] hover:bg-[#C41E3A]/10 group flex-shrink-0"
                data-testid={`category-icon-${cat.filter}`}
                onClick={(e) => {
                  if (isDragging) { e.preventDefault(); return; }
                  trackCategoryClick('home', cat.filter);
                }}
                draggable={false}
              >
                <picture>
                  <source srcSet="/logo-new-sm.webp" type="image/webp" />
                  <img 
                    src="/logo-new-sm.png" 
                    alt="" 
                    className="w-5 h-5 md:w-6 md:h-6 object-contain pointer-events-none"
                    loading="lazy"
                  />
                </picture>
                <span className="text-white/80 text-xs md:text-sm font-medium group-hover:text-white transition-colors whitespace-nowrap pointer-events-none">
                  {cat.name}
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Categories - Clean */}
      <section className="py-10 md:py-20 max-w-7xl mx-auto px-4" data-testid="categories-section">
        <div className="text-center mb-8 md:mb-12">
          <Link to="/produtos" onClick={() => trackAdClick('ver_colecoes', '/produtos')}>
            <Button 
              size="lg" 
              className="relative overflow-hidden bg-[#2d2d2d] hover:bg-[#3d3d3d] text-[#f7f7f7] rounded-full px-6 py-3 md:px-8 md:py-4 text-sm md:text-base font-semibold tracking-[0.1em] transition-all"
              style={{ fontFamily: "'Montserrat', sans-serif" }}
              data-testid="cta-ver-colecoes"
            >
              <span 
                className="absolute top-0 left-0 w-full h-full pointer-events-none overflow-hidden rounded-full"
              >
                <span 
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '50%',
                    height: '100%',
                    background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.08) 30%, rgba(255,255,255,0.3) 50%, rgba(255,255,255,0.08) 70%, transparent 100%)',
                    animation: 'shimmer-slide 2.8s ease-in-out infinite',
                  }}
                />
              </span>
              <span className="shimmer-effect" />
              Ver Todas Coleções
              <ArrowRight className="ml-2 h-3.5 w-3.5 md:ml-3 md:h-4 md:w-4 stroke-2 text-[#C41E3A]" />
            </Button>
          </Link>
        </div>

        <div className="grid grid-cols-3 gap-3 md:gap-6">
          {categories.map((category) => (
            <Link
              key={category.name}
              to={category.href}
              className="group relative aspect-[3/4] md:aspect-[3/4] overflow-hidden"
              data-testid={`category-${category.name.toLowerCase()}`}
            >
              <img
                src={category.image}
                alt={category.name}
                className="absolute inset-0 w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                loading="eager"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-3 md:p-6">
                <h3 className="text-sm md:text-2xl font-semibold text-[#F5F5F0] mb-0.5 md:mb-1 font-['Cormorant_Garamond']">
                  {category.name}
                </h3>
                <span className="hidden md:inline-flex items-center text-[#F5F5F0]/70 text-xs font-medium group-hover:text-[#F5F5F0] transition-colors">
                  Ver produtos
                  <ArrowRight className="ml-2 h-3 w-3 transition-transform duration-300 group-hover:translate-x-1" />
                </span>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Featured Products - Clean */}
      <section className="py-16 md:py-24 bg-white" data-testid="featured-section">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-end justify-between mb-10 md:mb-14">
            <div>
              <h2 className="text-2xl md:text-3xl font-semibold text-neutral-900 font-['Cormorant_Garamond']">
                Destaques
              </h2>
            </div>
            <Link 
              to="/produtos" 
              className="hidden md:flex items-center text-neutral-500 text-sm font-medium hover:text-neutral-800 transition-colors"
            >
              Ver todos
              <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </div>

          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="animate-pulse">
                  <div className="aspect-[4/5] bg-neutral-200 mb-4" />
                  <div className="h-3 bg-neutral-200 rounded mb-2" />
                  <div className="h-3 bg-neutral-200 rounded w-2/3" />
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
              {[...featuredProducts].sort((a, b) => {
                const aFav = favoriteIds.includes(a.product_id) ? 0 : 1;
                const bFav = favoriteIds.includes(b.product_id) ? 0 : 1;
                return aFav - bFav;
              }).map((product, index) => (
                <ProductCard 
                  key={product.product_id} 
                  product={product} 
                  isFavorited={favoriteIds.includes(product.product_id)}
                  onFavoriteChange={fetchFavorites}
                  priority={index < 4}
                />
              ))}
            </div>
          )}

          <div className="mt-10 text-center md:hidden">
            <Link to="/produtos" onClick={() => trackAdClick('ver_todos_mobile', '/produtos')}>
              <Button variant="outline" className="relative overflow-hidden border-neutral-300 text-neutral-700 hover:bg-neutral-100 hover:border-neutral-400 px-6 py-2.5 text-sm">
                <span 
                  className="absolute top-0 left-0 w-full h-full pointer-events-none overflow-hidden rounded-md"
                >
                  <span 
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '50%',
                      height: '100%',
                      background: 'linear-gradient(90deg, transparent 0%, rgba(0,0,0,0.04) 30%, rgba(0,0,0,0.1) 50%, rgba(0,0,0,0.04) 70%, transparent 100%)',
                      animation: 'shimmer-slide 2.8s ease-in-out infinite',
                    }}
                  />
                </span>
                Ver todos os produtos
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Cashback Banner */}
      <section className="py-12 md:py-16 bg-white" data-testid="cashback-section">
        <div className="max-w-7xl mx-auto px-4">
          <div className="bg-gradient-to-br from-emerald-800 via-emerald-900 to-emerald-950 rounded-2xl p-8 md:p-12 text-white shadow-xl">
            <div className="flex flex-col md:flex-row items-center gap-6 md:gap-8">
              <div className="w-16 h-16 md:w-20 md:h-20 bg-white/10 rounded-full flex items-center justify-center flex-shrink-0 backdrop-blur-sm">
                <Wallet className="h-10 w-10 md:h-12 md:w-12 text-emerald-200" />
              </div>
              <div className="text-center md:text-left flex-1">
                <h2 className="text-2xl md:text-3xl font-bold mb-2">Ganhe 10% de Cashback</h2>
                <p className="text-emerald-200 text-sm md:text-base">
                  A cada compra, 10% do valor volta para voce como credito. Use quando quiser, sem prazo de validade. Quanto mais compra, mais economiza!
                </p>
              </div>
              <div className="flex gap-3">
                <Link to="/produtos">
                  <Button className="bg-white text-emerald-900 hover:bg-emerald-50 rounded-full px-6 py-3 font-semibold text-sm" data-testid="cashback-cta-shop">
                    Comprar agora
                  </Button>
                </Link>
                <Link to="/minha-conta">
                  <Button variant="outline" className="border-white/30 text-white hover:bg-white/10 rounded-full px-6 py-3 font-semibold text-sm" data-testid="cashback-cta-account">
                    Minha conta
                  </Button>
                </Link>
              </div>
              <p className="text-emerald-300/80 text-xs md:text-sm mt-3">
                Crie sua conta e ganhe R$20,00 de bônus na hora!
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Newsletter - Premium minimal */}
      <section className="py-20 md:py-28 bg-[#1a1a1a]" data-testid="newsletter-section">
        <div className="max-w-2xl mx-auto px-4 text-center">
          <h2 className="text-2xl md:text-3xl font-semibold text-[#F5F5F0] mb-4 font-['Cormorant_Garamond']">
            Fique por Dentro
          </h2>
          <p className="text-neutral-500 mb-8 text-sm">
            Receba ofertas exclusivas e lançamentos em primeira mão
          </p>
          <form className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
            <input
              type="email"
              placeholder="Seu melhor e-mail"
              className="flex-1 px-4 py-3 bg-white/5 border border-white/10 text-white placeholder-neutral-500 text-sm rounded-sm focus:outline-none focus:border-white/30 transition-colors"
              data-testid="newsletter-email"
            />
            <Button 
              type="submit"
              className="bg-white hover:bg-neutral-100 text-[#0a0a0a] px-6 py-3 font-medium text-sm rounded-sm transition-colors"
              data-testid="newsletter-submit"
            >
              Cadastrar
            </Button>
          </form>
        </div>
      </section>

      {/* Footer - Profissional com dados da empresa */}
      <footer className="bg-[#1a1a1a] text-[#F5F5F0] pt-16 pb-8 border-t border-[#F5F5F0]/5" data-testid="footer">
        <div className="max-w-7xl mx-auto px-4">
          {/* Garantia em destaque */}
          <div className="bg-white/5 rounded-lg p-5 md:p-8 mb-12 border border-white/10">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-green-500/20 rounded-full flex items-center justify-center">
                  <RotateCcw className="h-6 w-6 text-green-400" />
                </div>
                <div>
                  <h3 className="font-semibold text-[#F5F5F0]">Garantia de Satisfação</h3>
                  <p className="text-sm text-neutral-400">Troque grátis em até 7 dias sem complicação</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="text-center">
                  <Shield className="h-5 w-5 text-green-400 mx-auto mb-1" />
                  <span className="text-[10px] text-neutral-500">Site Seguro</span>
                </div>
                <div className="text-center">
                  <Lock className="h-5 w-5 text-green-400 mx-auto mb-1" />
                  <span className="text-[10px] text-neutral-500">Dados Protegidos</span>
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-10 pb-12 border-b border-white/10">
            {/* Logo & About */}
            <div className="col-span-2 md:col-span-1">
              <div className="mb-4">
                <span className="text-xl font-semibold font-['Cormorant_Garamond'] text-[#F5F5F0]">OUTLET</span>
                <span className="block text-[9px] tracking-[0.25em] text-neutral-500 font-medium mt-1">PREMIUM</span>
              </div>
              <p className="text-neutral-500 text-xs leading-relaxed mb-4">
                Moda de qualidade com preços acessíveis. Desde 2020 levando estilo para todo o Brasil.
              </p>
              {/* Bandeiras de pagamento no footer - versão otimizada */}
              <div className="mt-3">
                <img 
                  src="/payment_methods.jpg" 
                  alt="Visa, Master, Elo, Amex, Hiper, PIX, Boleto"
                  className="h-4 w-auto object-contain opacity-90"
                  loading="lazy"
                />
              </div>
            </div>

            {/* Links */}
            <div>
              <h4 className="font-medium text-xs tracking-wider mb-4 text-white uppercase">Categorias</h4>
              <ul className="space-y-2 text-xs">
                <li><Link to="/produtos/masculino" className="text-white/80 hover:text-white transition-colors">Masculino</Link></li>
                <li><Link to="/produtos/feminino" className="text-white/80 hover:text-white transition-colors">Feminino</Link></li>
                <li><Link to="/produtos/infantil" className="text-white/80 hover:text-white transition-colors">Infantil</Link></li>
                <li><Link to="/produtos" className="text-white/80 hover:text-white transition-colors">Todos os Produtos</Link></li>
              </ul>
            </div>

            <div>
              <h4 className="font-medium text-xs tracking-wider mb-4 text-white uppercase">Institucional</h4>
              <ul className="space-y-2 text-xs">
                <li><Link to="/sobre" className="text-white/80 hover:text-white transition-colors">Quem Somos / Segurança</Link></li>
                <li><Link to="/sobre#privacidade" className="text-white/80 hover:text-white transition-colors">Política de Privacidade</Link></li>
                <li><Link to="/sobre#termos" className="text-white/80 hover:text-white transition-colors">Termos de Uso</Link></li>
                <li><Link to="/sobre#trocas" className="text-white/80 hover:text-white transition-colors">Trocas e Devoluções</Link></li>
              </ul>
            </div>

            <div>
              <h4 className="font-medium text-xs tracking-wider mb-4 text-white uppercase">Atendimento</h4>
              <ul className="space-y-2 text-white/80 text-xs">
                <li>
                  <Link to="/rastreio" className="inline-flex items-center gap-1.5 text-violet-300 hover:text-violet-200 font-semibold transition-colors" data-testid="footer-rastreio">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-3.5 h-3.5">
                      <path d="M10 17h4V5H2v12h3"/><path d="M20 17h2v-3.34a4 4 0 0 0-1.17-2.83L19 9h-5v8h1"/><circle cx="7.5" cy="17.5" r="2.5"/><circle cx="17.5" cy="17.5" r="2.5"/>
                    </svg>
                    Rastrear pedido
                  </Link>
                </li>
                <li>Segunda a Sexta: 9h às 18h</li>
                <li>WhatsApp: (11) 99999-9999</li>
                <li>contato@outletpremium.com</li>
              </ul>
            </div>
          </div>

          {/* Rodapé com CNPJ e dados legais */}
          <div className="pt-8 text-center space-y-2">
            <p className="text-neutral-600 text-[10px] tracking-wider">
              © 2025 Outlet Premium. Todos os direitos reservados.
            </p>
            <p className="text-neutral-700 text-[10px]">
              OUTLET PREMIUM COMÉRCIO DE ROUPAS LTDA • CNPJ: 00.000.000/0001-00
            </p>
            <p className="text-neutral-700 text-[9px]">
              Rua Exemplo, 123 - Centro - São Paulo/SP - CEP: 01000-000
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
